<template>
  <div class="product-widget mb-30">
    <h5 class="pt-title">Product Categories</h5>
    <div class="widget-category-list mt-20">
      <div class="single-widget-category pointer" v-for="c in categoryData" :key="c.id">
        <input
          type="radio"
          name="product-category-filter"
          :id="`sidebar-pc-${c.id}`"
          :value="c.id"
          :checked="String(activeQuery) === String(c.id)"
          @change="handleCategoryRoute(c.id)"
        >
        <label :for="`sidebar-pc-${c.id}`" class="pointer">
          {{ c.name }}
        </label>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductCategories",
  data() {
    return {
      categoryData: [], // Initialize as an empty array
      activeQuery: "",
    }
  },
  watch: {
    "$route.query.web_category": {
      handler(newWebCategory) {
        this.fetchCategories(newWebCategory) // Fetch categories based on new web_category
      },
      immediate: true, // Trigger on initial load
    },
    "$route.query": {
      handler(newQuery) {
        this.activeQuery = newQuery.category || ''
        console.log('Active Query:', this.activeQuery)
      },
      immediate: true,
    },
  },
  methods: {
    async fetchCategories(web_category) {
      const wc =
        web_category != null && String(web_category).trim() !== ''
          ? String(web_category).trim()
          : String(this.$route.query.web_category ?? '').trim()
      if (!wc) {
        this.categoryData = []
        return
      }

      try {
        const response = await this.$axios.get('/website-prodcut-categories', {
          params: { web_category: wc },
        })
        this.categoryData = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error("Error fetching categories:", error)
        this.categoryData = []
      }
    },
    handleCategoryRoute(value) {
      const v = String(value)
      if (String(this.$route.query.category) === v) return
      this.activeQuery = v
      const currentQuery = { ...this.$route.query }
      currentQuery.category = v

      this.$router.push({
        path: '/shop',
        query: currentQuery,
      }).catch(() => {})
    },
  },
  mounted() {
    this.activeQuery = this.$route.query.category

    this.fetchCategories() // Fetch categories when the component is mounted
  },
}
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

/* Luxury selectable cards for filters */
.product-widget {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 1) 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  box-shadow: 0 14px 36px rgba(2, 6, 23, 0.06);
  padding: 16px;
}

.pt-title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 900;
  letter-spacing: -0.01em;
  margin: 0 0 14px 0;
  box-shadow: none !important;
}

.pt-title::before,
.pt-title::after {
  content: none !important;
  display: none !important;
}

/* Selected-state indicator: small round icon + check */
.single-widget-category label::before {
  content: "";
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(15, 23, 42, 0.16);
  background: rgba(15, 23, 42, 0.03);
  transition: 0.2s ease;
}

.single-widget-category label::after {
  content: "✓";
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 50%;
  color: #ffffff;
  font-size: 12px;
  font-weight: 900;
  opacity: 0;
  transition: 0.2s ease;
}

.widget-category-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.single-widget-category {
  position: relative;
}

.single-widget-category input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.single-widget-category label {
  cursor: pointer;
  user-select: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  position: relative; /* anchor pseudo-element icon */
  padding: 10px 12px 10px 42px; /* space for round indicator */
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.55);
  transition: 0.2s ease;
  color: rgba(15, 23, 42, 0.88);
}

.single-widget-category label:hover {
  transform: translateY(-1px);
  border-color: rgba(252, 190, 0, 0.55);
}

.single-widget-category input:checked + label {
  border-width: 2px;
  border-color: var(--fe-accent, #fcbe00);
  color: #0f172a;
  font-weight: 800;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 42%, transparent) 0%,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 18%, rgba(255, 255, 255, 0.97)) 52%,
    rgba(255, 255, 255, 0.94) 100%
  );
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--fe-accent, #fcbe00) 35%, transparent) inset,
    0 10px 28px color-mix(in srgb, var(--fe-accent, #fcbe00) 28%, transparent),
    0 20px 48px rgba(15, 23, 42, 0.08);
}

.single-widget-category input:checked + label::before {
  border-color: color-mix(in srgb, var(--fe-accent, #fcbe00) 90%, #111);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 96%, #fff) 0%,
    color-mix(in srgb, var(--fe-accent-hover, #e5ac00) 85%, #ff7a00) 100%
  );
  box-shadow:
    0 0 0 2px rgba(255, 255, 255, 0.55) inset,
    0 10px 22px color-mix(in srgb, var(--fe-accent, #fcbe00) 35%, transparent);
}

.single-widget-category input:checked + label::after {
  opacity: 1;
}
</style>